﻿import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import { listCustomers, type CustomerListItem } from "@/lib/api";
import ClienteTable from "./components/ClienteTable";
import ImportClientesModal from "./components/ImportClientesModal";
import NuevoClienteModal from "./components/NuevoClienteModal";

function customerMatchesSearch(customer: CustomerListItem, query: string): boolean {
  return (
    customer.nombre.toLowerCase().includes(query) ||
    customer.rut.toLowerCase().includes(query) ||
    customer.direccion.toLowerCase().includes(query) ||
    customer.contracts.some((contract) => contract.contractNumber.toLowerCase().includes(query))
  );
}

function formatCustomerContracts(customer: CustomerListItem): string {
  const contracts = customer.contracts.map((contract) => contract.contractNumber).filter(Boolean);
  return contracts.length > 0 ? contracts.join(", ") : "Sin contrato";
}

export default function ClientesPage() {
  const { session } = useSession();
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [rows, setRows] = useState<CustomerListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setIsLoading(true);
      setError(null);
      try {
        const customers = await listCustomers(session);
        if (!cancelled) setRows(customers);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudo cargar clientes");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, session]);

  const stats = useMemo(() => {
    const total = rows.length;
    const activos = rows.filter((r) => r.estado === "activo").length;
    const morosos = rows.filter((r) => r.estado === "moroso").length;
    const cortados = rows.filter((r) => r.estado === "cortado").length;
    return { total, activos, morosos, cortados };
  }, [rows]);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [] as CustomerListItem[];
    return rows
      .filter((c) => customerMatchesSearch(c, q))
      .slice(0, 12);
  }, [rows, search]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [] as CustomerListItem[];
    return rows.filter((c) => customerMatchesSearch(c, q));
  }, [rows, search]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Gestión de clientes</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gestión de clientes, historial y compromisos de pago</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-2-line" /></div>
            Importar
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line" /></div>
            Nuevo Cliente
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Clientes", value: stats.total, icon: "ri-user-line", color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Activos", value: stats.activos, icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Morosos", value: stats.morosos, icon: "ri-error-warning-line", color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Cortados", value: stats.cortados, icon: "ri-scissors-cut-line", color: "text-red-600", bg: "bg-red-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg} flex-shrink-0`}>
              <i className={`${s.icon} text-lg ${s.color}`} />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-800">{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre, RUT, dirección o contrato (mínimo 2 caracteres)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
          />
        </div>

        {search.trim().length >= 2 && suggestions.length > 0 && (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            {suggestions.map((c) => (
              <button
                key={c.id}
                onClick={() => setSearch(c.rut)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
              >
                <span className="font-medium text-gray-800">{c.nombre}</span>
                <span className="text-gray-500"> • {c.rut}</span>
                <span className="text-gray-400"> • {formatCustomerContracts(c)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="text-xs text-gray-500">
          {search.trim().length < 2 ? "Escribe al menos 2 caracteres para buscar clientes." : `${filteredRows.length} resultado(s)`}
        </div>
      </div>

      <ClienteTable rows={filteredRows} isLoading={isLoading} error={error} />

      {showModal && (
        <NuevoClienteModal
          onClose={() => setShowModal(false)}
          onCreated={() => setReloadKey((prev) => prev + 1)}
        />
      )}

      {showImportModal && (
        <ImportClientesModal
          onClose={() => setShowImportModal(false)}
          onImported={() => setReloadKey((prev) => prev + 1)}
        />
      )}
    </div>
  );
}
